import Tippy from '@tippyjs/react'
import clsx from 'clsx'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { confirmDialog } from 'primereact/confirmdialog'
import { DataTable, DataTableFilterMetaData } from 'primereact/datatable'
import { Fragment, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ActionColumn, BaseManagemenColumnProps } from './types'

interface IProps {
  dataSource: any[]
  columns: BaseManagemenColumnProps[]
  loading?: boolean
  filters: any
  sortObject: any
  rowClassName?: (data) => string
  showActionColumn: boolean
  actionWidth?: any
  actionColumns?: ActionColumn[]
  setRestSearchObject: (obj: any) => void
}

const TableView = ({
  dataSource,
  columns,
  loading,
  filters,
  sortObject,
  rowClassName,
  showActionColumn,
  actionWidth,
  actionColumns,
  setRestSearchObject,
}: IProps) => {
  const tableRef = useRef<any>(null)
  useEffect(() => {
    const table = tableRef.current?.getTable?.()
    if (!table) return
    Array.from(table.getElementsByTagName('td'))?.forEach((td: HTMLElement) => {
      td.setAttribute('title', td.innerText || td.textContent)
    })
  }, [dataSource])

  return (
    <DataTable
      ref={tableRef}
      scrollable
      value={dataSource}
      className="mt-3"
      removableSort
      loading={loading}
      filters={filters}
      emptyMessage={t('No available options')}
      rowClassName={rowClassName}
      loadingIcon={
        <div className="absolute w-full h-full">
          <div className="skeleton-container">
            <div className="skeleton-placeholder skeleton-title"></div>
            <div className="skeleton-placeholder skeleton-content"></div>
            <div className="skeleton-placeholder skeleton-content"></div>
            <div className="skeleton-placeholder skeleton-content"></div>
            <div className="skeleton-placeholder skeleton-content"></div>
            <div className="skeleton-placeholder skeleton-content"></div>
          </div>
        </div>
      }
      onFilter={(e) => {
        const valueObject = {}
        Object.keys(e.filters).forEach((key) => {
          valueObject[key] = (e.filters[key] as DataTableFilterMetaData)?.value
        })
        setRestSearchObject(valueObject)
      }}
      lazy
      {...sortObject}
      onSort={(e) => {
        setRestSearchObject({
          sortBy: e.sortOrder ? `"${e.sortField}":${e.sortOrder}` : null,
        })
      }}
    >
      {columns.map((col) => (
        <Column
          {...col}
          filterApply={(options) => (
            <Button label={t('Apply')} onClick={options.filterApplyCallback}></Button>
          )}
          filterClear={(options) => (
            <Button outlined label={t('Clear')} onClick={options.filterClearCallback}></Button>
          )}
          field={col.dataIndex ?? col.key}
          key={col.key}
          style={col.width ? { width: col.width } : null}
        ></Column>
      ))}
      {showActionColumn ? (
        <Column
          header={t('Action')}
          field="action"
          style={{ width: actionWidth ?? '12%' }}
          body={(rowData) => (
            <div className="flex gap-2">
              {actionColumns?.map((btn) => {
                const btnContent =
                  btn.href && !btn.disabled?.(rowData) ? (
                    <Link
                      key={btn.key}
                      to={btn.href(rowData)}
                      onClick={btn.action}
                      target={btn.linkTarget}
                      className="flex justify-content-center align-items-center cursor-pointer text-gray-500 no-underline"
                      style={{ width: 20, height: 20 }}
                    >
                      {btn.icon}
                    </Link>
                  ) : (
                    <div
                      key={btn.key}
                      onClick={
                        btn.disabled && btn.disabled(rowData)
                          ? null
                          : () =>
                              btn.showConfirm
                                ? confirmDialog({
                                    message: t('doyouwant', { action: t('delele this record') }),
                                    header: t('confirmation', { action: t('Delete') }),
                                    defaultFocus: 'reject',
                                    acceptClassName: 'p-button-danger',
                                    accept: () => btn.action(rowData),
                                    acceptLabel: t('Yes'),
                                    rejectLabel: t('No'),
                                    ...btn.showConfirm,
                                  })
                                : btn.action(rowData)
                      }
                      className={clsx(
                        'flex justify-content-center align-items-center cursor-pointer ',
                        btn.disabled && btn.disabled(rowData) ? 'text-gray-200 ' : 'text-gray-900'
                      )}
                      style={{ width: 20, height: 20 }}
                    >
                      {btn.icon}
                    </div>
                  )
                return btn.tooltip ? (
                  <Tippy key={btn.key} content={btn.tooltip}>
                    {btnContent}
                  </Tippy>
                ) : (
                  <Fragment key={btn.key}>{btnContent}</Fragment>
                )
              })}
            </div>
          )}
        ></Column>
      ) : null}
    </DataTable>
  )
}

export default TableView
